import { useMemo } from 'react'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'
import { Badge } from '@wartek-id/badge'
import type { BadgeProps } from '@wartek-id/badge'

import { Header } from 'app/KinerjaShared/components/Header'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { EvaluationResult as EvaluationResultV2 } from 'app/KinerjaShared/components/EvaluationResult'
import BannerStatusPengaliranAkhir from 'app/KinerjaPage/components/BannerStatusPengaliranAkhir'
import {
  useApiGetRiwayatTahunanByYear,
  useApiGetRiwayatHasilEvaluasiTahunan,
} from 'api/kinerja/kepala-sekolah/riwayat'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import { PANDUAN_GENERAL_URL } from 'app/KinerjaShared/utils/panduan'

type HasilPenilaianKinerjaProps = {
  npsn: string
}
export function HasilPenilaianKinerja({ npsn }: HasilPenilaianKinerjaProps) {
  const { periodYear: currentYear } = usePeriodContext()
  const { profile } = useRoleContext()

  // ------------------------------
  // Get Riwayat Tahunan
  // ------------------------------
  const { isLoading: isRiwayatTahunanLoading, data: riwayatData } =
    useApiGetRiwayatTahunanByYear(
      { year: `${currentYear}`, npsn },
      {
        enabled: !!currentYear,
        staleTime: 60 * 60 * 1000, // 1 hour
        useErrorBoundary: true,
      }
    )
  // Find riwayatId by npsn
  const riwayatId = riwayatData?.data?.find((item) => item.npsn === npsn)?.id

  // ------------------------------
  // Get Hasil Evaluasi Tahunan
  // ------------------------------
  const {
    data: { data: hasilEvaluasiData } = {
      data: null,
      meta: null,
    },
    isLoading: isHasilEvaluasiLoading,
  } = useApiGetRiwayatHasilEvaluasiTahunan(riwayatId, {
    enabled: !!riwayatId,
    useErrorBoundary: true,
  })
  const isLoading = isRiwayatTahunanLoading || isHasilEvaluasiLoading
  const predicate = hasilEvaluasiData?.predikatKinerja?.predikatKinerja
  const workBehavior = hasilEvaluasiData?.predikatKinerja?.perilakuKerja
  const performancePractice = hasilEvaluasiData?.predikatKinerja?.praktikKinerja

  // ------------------------------
  // Show Banner Status Pengaliran
  // ------------------------------
  const showBannerStatusPengaliran = useMemo(() => {
    const roleInCurrentNpsn = profile?.roles?.find(
      (role) => role.npsn === npsn
    )?.type

    // Only show banner pengaliran if role is KS_DEFINITIF
    return roleInCurrentNpsn === 'KS_DEFINITIF'
  }, [profile?.roles, npsn])

  return (
    <main className="bg-gray-10 min-h-full">
      <header className="bg-white pt-11 pb-6 z-[2] relative">
        <div className="max-w-7xl mx-auto px-8">
          <Header
            title="Hasil Penilaian Kinerja Pegawai"
            rightNode={
              <Button
                variant="ghost"
                className="flex gap-1.5 !py-4 rounded print:hidden"
                as="a"
                type={undefined}
                href={PANDUAN_GENERAL_URL}
                target="blank"
              >
                <Icon fontSize="sm" color="informational">
                  help
                </Icon>
                Baca panduan
              </Button>
            }
          />
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-8 py-11 w-full flex flex-col space-y-6">
        {isLoading ? (
          <div className="flex items-center justify-center px-9 pt-9 gap-5 h-96">
            <Spinner />
            <Text variant="heading-md">Dokumen sedang disiapkan.</Text>
          </div>
        ) : (
          <>
            {showBannerStatusPengaliran && (
              <BannerStatusPengaliranAkhir year={currentYear} />
            )}
            <EvaluationResultV2
              title="Hasil Penilaian Kinerja Kepala Sekolah"
              dinas={hasilEvaluasiData?.namaInstansi}
              subordinate={{
                name: hasilEvaluasiData?.ks?.nama,
                nip: hasilEvaluasiData?.ks?.nip,
                departmentLevel: hasilEvaluasiData?.ks?.pangkatGolongan,
                role: hasilEvaluasiData?.ks?.jabatan,
                schoolName: hasilEvaluasiData?.ks?.unitKerja,
              }}
              supervisor={{
                name: hasilEvaluasiData?.dinas?.nama,
                nip: hasilEvaluasiData?.dinas?.nip,
                departmentLevel: hasilEvaluasiData?.dinas?.pangkatGolongan,
                role: hasilEvaluasiData?.dinas?.jabatan,
                schoolName: hasilEvaluasiData?.dinas?.unitKerja,
              }}
              year={currentYear}
              predicate={predicate}
              workBehaviorLabel={
                !!workBehavior && <WorkBehaviorBadge value={workBehavior} />
              }
              performancePracticeLabel={
                !!performancePractice && (
                  <PerformancePracticeBadge value={performancePractice} />
                )
              }
            />
          </>
        )}
      </div>
    </main>
  )
}

const BADGE_VARIANTS: Record<string, BadgeProps['variant']> = {
  'Sesuai Ekspektasi': 'neutral',
  'Di Atas Ekspektasi': 'success',
  'Di Bawah Ekspektasi': 'critical',
}

function WorkBehaviorBadge({ value }: { value: string }) {
  return (
    <Badge variant={BADGE_VARIANTS[value] || 'neutral'}>{value || '-'}</Badge>
  )
}

function PerformancePracticeBadge({ value }: { value: string }) {
  return (
    <Badge variant={BADGE_VARIANTS[value] || 'neutral'}>{value || '-'}</Badge>
  )
}
